// @flow

import React, { useEffect, useState } from 'react';

import { Button, Col, Input, Row } from 'antd';
import classNames from 'classnames';
import telephoneInverted from '_images/telephone-inverted.svg';
import clock from '_images/clock.svg';
import appAndroid from '_images/app-android.svg';
import appIOS from '_images/app-ios.svg';
import envelope from '_images/envelope.svg';
import telephone from '_images/telephone.svg';

import './style.less';
import { isElementInViewport } from '_utils/common';

type Props = {
  quoteButtonText: string,
  guideSection?: React.Node,
  mainFormRef?: Object,
  onClickGetQuote?: () => void,
};

const LeadPageFooter = ({
  quoteButtonText,
  guideSection,
  mainFormRef,
  onClickGetQuote,
}: Props) => {
  const [showFooterForm, setShowFooterForm] = useState(false);
  useEffect(() => {
    if (!mainFormRef || !mainFormRef.current) return;
    const onScroll = () => {
      setShowFooterForm(!isElementInViewport(mainFormRef.current));
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleClickGetQuote = () => {
    window.scrollTo(0, 0);
    if (onClickGetQuote) {
      onClickGetQuote();
    }
  };

  return (
    <div className="lead-page-footer-wrapper">
      <div className="lead-page-footer p-4 md:p-7">
        {guideSection}
        {guideSection ? <hr className="no-margin" /> : null}
        <div className="pt-7">
          <Row>
            <Col xs={{ span: 24 }} sm={{ span: 8 }}>
              <div className="font-bold os-text-19">Contact us</div>
              <div className="mt-3 flex justify-start items-center">
                <img src={telephone} height="12" width="12" alt="6533 5878" />
                <div className="ml-2">
                  <b>6533 5878</b> (main)
                </div>
              </div>
              <div className="flex justify-start items-center">
                <img
                  src={envelope}
                  height="14"
                  width="14"
                  alt="autos.assist@carousell.com"
                />
                <div className="ml-2">
                  <a href="mailto: autos.assist@carousell.com">
                    autos.assist@carousell.com
                  </a>
                </div>
              </div>
            </Col>
            <Col xs={{ span: 24 }} sm={{ span: 8 }}>
              <div className="font-bold os-text-19 mt-5 md:mt-0">
                Operating Hours
              </div>
              <div className="mt-3 table">
                <div className="pr-2 table-cell">
                  <div className="inline-block">
                    <img
                      src={clock}
                      height="12"
                      width="12"
                      alt="10:00 AM - 06:00 PM, Monday to Friday"
                    />
                  </div>
                </div>
                <div className="table-cell ml-2">
                  10:00 AM - 06:00 PM, Monday to Friday
                  <br className="hidden sm:block" />
                  (Except Weekends and P.H.)
                </div>
              </div>
            </Col>
            <Col xs={{ span: 24 }} sm={{ span: 8 }}>
              <div className="font-bold os-text-19 mt-5 md:mt-0">
                Download app
              </div>
              <div className="mt-3 flex justify-start gap-1">
                <a
                  href="https://apps.apple.com/us/app/carousell-snap-sell-chat-buy/id548607187?itsct=apps_box_badge&amp;itscg=30200"
                  target="_blank"
                >
                  <img
                    data-src={appIOS}
                    alt="Download on the App Store"
                    width="144"
                    height="48"
                    src={appIOS}
                    loading="lazy"
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.thecarousell.Carousell&amp;pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1"
                  target="_blank"
                >
                  <img
                    data-src={appAndroid}
                    alt="Get it on Google Play"
                    width="162"
                    height="48"
                    src={appAndroid}
                    loading="lazy"
                  />
                </a>
              </div>
            </Col>
          </Row>
          <div className="font-bold os-text-19 mt-6">Carousell</div>
          <Row className="mt-3">
            <Col xs={{ span: 24 }} sm={{ span: 8 }}>
              <div>
                <a
                  href="https://www.carousell.sg/"
                  className="text-default"
                  target="_blank"
                >
                  Browse on Carousell
                </a>
              </div>
              <div>
                <a
                  href="https://www.carousell.sg/used-cars-singapore/h-1233?t-id=100156_1625404719972&amp;t-source=top_navigation_bar"
                  className="text-default"
                  target="_blank"
                >
                  Cars for sale in Singapore
                </a>
              </div>
              <div>
                <a
                  href="https://www.carousell.sg/car-accessories-servicing-workshops/h-136/"
                  className="text-default"
                  target="_blank"
                >
                  Car accessories for sale in Singapore
                </a>
              </div>
            </Col>
            <Col xs={{ span: 24 }} sm={{ span: 8 }}>
              <div>
                <a
                  href="https://www.carousell.sg/motorcycles/h-133/"
                  className="text-default"
                  target="_blank"
                >
                  Motorcycles for sale in Singapore
                </a>
              </div>
              <div>
                <a
                  href="https://www.carousell.sg/car-rental-singapore/h-1241/"
                  className="text-default"
                  target="_blank"
                >
                  Car rental in Singapore
                </a>
              </div>
            </Col>
          </Row>
          <hr className="mt-5 mb-5" />
          <p className="text-center mt-2 copyright">
            Copyright © 2022 OneShift Pte Ltd <br className="sm:hidden" />
            All Rights Reserved
          </p>
        </div>
      </div>
      <div
        className={classNames(
          'footer-fixed-section',
          'transition-opacity',
          showFooterForm ? 'opacity-100' : 'opacity-0'
        )}
      >
        <div className="footer-fixed-section-content w-full h-full flex items-center">
          <Row gutter={30} className="w-full">
            <Col xs={{ span: 24 }} lg={{ span: 16 }}>
              <div className="hidden lg:block">
                <Row gutter={30}>
                  <Col md={{ span: 8 }}>
                    <Input
                      size="large"
                      type="text"
                      className="form-control"
                      id="catfish-owner-name"
                      placeholder="Enter name"
                    />
                  </Col>
                  <Col md={{ span: 8 }}>
                    <Input
                      size="large"
                      type="text"
                      className="form-control"
                      id="catfish-mobile"
                      placeholder="Enter mobile"
                    />
                  </Col>
                  <Col md={{ span: 8 }}>
                    <Input
                      size="large"
                      type="text"
                      className="form-control"
                      id="catfish-vehicle-no"
                      placeholder="Enter vehicle no."
                    />
                  </Col>
                </Row>
              </div>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Row gutter={30}>
                <Col xs={{ span: 12 }}>
                  <Button
                    className="get-quote-button"
                    type="primary"
                    size="large"
                    onClick={handleClickGetQuote}
                  >
                    {quoteButtonText}
                  </Button>
                </Col>
                <Col xs={{ span: 12 }}>
                  <a href="tel: 65335878" className="w-full">
                    <Button
                      type="primary"
                      className="w-full contact-button"
                      size="large"
                    >
                      <div className="flex items-center justify-center">
                        <div>
                          <img
                            className="inline-block"
                            src={telephoneInverted}
                            height="10"
                            width="10"
                            alt="6533 5878"
                            loading="lazy"
                          />
                          <span className="ml-2 font-semibold">6533 5878</span>
                        </div>
                      </div>
                    </Button>
                  </a>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default LeadPageFooter;
